import { ActionIcon, Anchor, Box, Card, Group, Loader, Menu, Stack, Table, Text, Badge } from '@mantine/core';
import { IconDotsVertical, IconFileDownload, IconFileText, IconLink } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react';
import type { Attachment, DiagnosticReport, DocumentReference, Observation, Reference } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';
import type { LabOrderResultsTimelineGroup } from '@/components/Timeline/types';
import { CLINICAL_PDF_REPORT_CODING } from '@bariendo/lib/codings';
import { downloadAttachment } from '@/services/attachments';
import { formatCodeableConcept, formatObservationValue, formatRange, getReferenceString } from '@medplum/core';
import { HighlightableItem } from '@/components/Timeline/TimelineItems/HighlightableItem';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';
import { RestrictedContentManager } from '@/pages/PatientTimelinePage/RestrictedContentManager';
import { ConfidentialityLevel } from '@bariendo/lib/constants';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { MedplumClient, WithId } from '@medplum/core';
import { TeammateAvatar } from '@/components/TeamAvatar';

async function loadObservationResults(
  medplum: MedplumClient,
  references: Reference<Observation>[]
): Promise<Observation[]> {
  if (!references || references.length === 0) {
    return [];
  }
  const observationResultsPromises = references.map((ref) => medplum.readReference(ref).catch(() => null));
  const observationResults = (await Promise.all(observationResultsPromises)) as (WithId<Observation> | null)[];
  const validObservations = observationResults.filter(
    (obs): obs is WithId<Observation> => obs !== null && obs.resourceType === 'Observation'
  );
  return validObservations as Observation[];
}

function isClinicalPdfObservation(obs: Observation): boolean {
  return Boolean(
    obs.code?.coding?.some(
      (c) => c.system === CLINICAL_PDF_REPORT_CODING.system && c.code === CLINICAL_PDF_REPORT_CODING.code
    )
  );
}

function getClinicalPdfValueAttachment(obs: Observation): Attachment | undefined {
  const withAttachment = obs as Observation & { valueAttachment?: Attachment };
  return withAttachment.valueAttachment?.url ? withAttachment.valueAttachment : undefined;
}

async function loadDiagnosticReportData(
  medplum: MedplumClient,
  report: DiagnosticReport
): Promise<{ observations: Observation[]; resultsPdfAttachment?: Attachment }> {
  const presented = report.presentedForm?.[0];

  if (!report.result || report.result.length === 0) {
    return {
      observations: [],
      resultsPdfAttachment: presented?.url ? presented : undefined,
    };
  }

  const topLevelObservations = await loadObservationResults(medplum, report.result);

  let resultsPdfAttachment: Attachment | undefined = presented?.url ? presented : undefined;
  if (!resultsPdfAttachment) {
    for (const obs of topLevelObservations) {
      if (!isClinicalPdfObservation(obs)) {
        continue;
      }
      const valueAttachment = getClinicalPdfValueAttachment(obs);
      if (valueAttachment) {
        resultsPdfAttachment = valueAttachment;
        break;
      }
      const derivedRef = obs.derivedFrom?.[0];
      if (derivedRef) {
        const doc = (await medplum.readReference(derivedRef).catch(() => null)) as DocumentReference | null;
        if (doc?.resourceType === 'DocumentReference') {
          const att = doc.content?.[0]?.attachment;
          if (att?.url) {
            resultsPdfAttachment = att;
            break;
          }
        }
      }
    }
  }

  // Healthie lab panels often use leaf Observations in `result` with no `hasMember`; other feeds use panel Observations whose `hasMember` holds row Observations.
  const observations: Observation[] = [];
  for (const obs of topLevelObservations) {
    if (isClinicalPdfObservation(obs)) {
      continue;
    }
    const members = obs.hasMember;
    if (members && members.length > 0) {
      observations.push(...(await loadObservationResults(medplum, members as Reference<Observation>[])));
    } else {
      observations.push(obs);
    }
  }

  return { observations, resultsPdfAttachment };
}

interface DiagnosticReportTimelineItemProps {
  group: LabOrderResultsTimelineGroup;
  highlightRef: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  handleNavigateToItem: (itemReference: string) => void;
}

export function DiagnosticReportTimelineItem({
  group,
  highlightRef,
  handleNavigateToItem,
  copyDirectLink,
}: DiagnosticReportTimelineItemProps) {
  const medplum = useMedplum();
  const {
    currentUserPermissions: { canAccessRestrictedContent },
  } = useCurrentUser();
  const item = group.items[0];
  const diagnosticReport = item.diagnosticReport;
  const [observations, setObservations] = useState<Observation[]>([]);
  const [resultsPdfAttachment, setResultsPdfAttachment] = useState<Attachment | undefined>();
  const [labOrderCommunicationRef, setLabOrderCommunicationRef] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const isRestrictedContent =
    (item.hiddenPayload || item.confidentiality === ConfidentialityLevel.RESTRICTED) && !canAccessRestrictedContent;

  useEffect(() => {
    async function loadResources() {
      try {
        setIsLoading(true);
        setObservations([]);
        setResultsPdfAttachment(undefined);

        const { observations: loadedObservations, resultsPdfAttachment: pdfAttachment } =
          await loadDiagnosticReportData(medplum, diagnosticReport);
        setObservations(loadedObservations);
        setResultsPdfAttachment(pdfAttachment);

        if (diagnosticReport.basedOn && diagnosticReport.basedOn[0]) {
          const serviceRequestRef = diagnosticReport.basedOn[0].reference;
          if (serviceRequestRef) {
            const communication = await medplum.searchOne('Communication', {
              'based-on': serviceRequestRef,
              _count: '1',
            });
            if (communication) {
              setLabOrderCommunicationRef(getReferenceString(communication));
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading DiagnosticReport resources', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadResources();
  }, [medplum, diagnosticReport]);

  const performerRef = diagnosticReport.performer?.[0];
  const performerName = performerRef?.display || 'Unknown Lab';

  const getStatusBadgeProps = (status: DiagnosticReport['status']) => {
    switch (status) {
      case 'final':
      case 'amended':
        return { color: 'green', text: 'COMPLETE' };
      case 'preliminary':
        return { color: 'yellow', text: 'PRELIMINARY' };
      case 'registered':
      case 'partial':
        return { color: 'blue', text: 'IN PROGRESS' };
      case 'cancelled':
      case 'entered-in-error':
        return { color: 'red', text: 'CANCELLED' };
      default:
        return { color: 'gray', text: status?.toUpperCase() || 'UNKNOWN' };
    }
  };

  const statusBadge = getStatusBadgeProps(diagnosticReport.status);

  const displayedObservations = isExpanded ? observations : observations.slice(0, 3);

  const handleViewLabOrder = () => {
    if (labOrderCommunicationRef) {
      handleNavigateToItem(labOrderCommunicationRef);
    }
  };

  const handleDownloadResultsPdf = () => {
    if (resultsPdfAttachment?.url) {
      void downloadAttachment(medplum, resultsPdfAttachment);
    }
  };

  const hasResultsPdf = Boolean(resultsPdfAttachment?.url);
  const hasObservationRows = observations.length > 0;

  return (
    <HighlightableItem itemRef={getReferenceString(item.reference) || ''} highlightRef={highlightRef}>
      <RestrictedContentManager isRestricted={isRestrictedContent}>
        <Card withBorder padding={0} radius="lg" bg="white">
          <Box>
            <Group w="100%" p="xs" justify="space-between" align="center" style={{ borderBottom: '1px solid #DEE2E6' }}>
              <Group gap="xs">
                <Stack gap={2}>
                  <Group gap="xs">
                    <Text size="md" fw={700}>
                      Lab results
                    </Text>
                    <Badge variant="light" color={statusBadge.color} radius="xl" size="sm" tt="uppercase">
                      {statusBadge.text}
                    </Badge>
                  </Group>
                </Stack>
              </Group>
              <Group gap="md" align="center">
                <Menu shadow="md" width={200} position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="sm" style={{ flexShrink: 0 }}>
                      <IconDotsVertical size={24} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconLink size={18} color="#246A7A" />}
                      onClick={() => copyDirectLink(getReferenceString(item.reference)!)}
                    >
                      Copy link
                    </Menu.Item>
                    {hasResultsPdf && (
                      <Menu.Item
                        leftSection={<IconFileDownload size={18} color="#246A7A" />}
                        onClick={handleDownloadResultsPdf}
                      >
                        Download PDF
                      </Menu.Item>
                    )}
                    {labOrderCommunicationRef && (
                      <Menu.Item leftSection={<IconFileText size={18} color="#246A7A" />} onClick={handleViewLabOrder}>
                        View original lab order
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
                <DateTimeTooltip date={item.timestamp} />
                <TeammateAvatar practitioner={performerRef} label={performerName} />
              </Group>
            </Group>

            <Box p="sm">
              {isLoading ? (
                <Group justify="center" p="md">
                  <Loader size="sm" />
                </Group>
              ) : !hasObservationRows && !hasResultsPdf ? (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No test results available yet
                </Text>
              ) : !hasObservationRows ? (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No structured results table for this report. Download PDF is available in the menu.
                </Text>
              ) : (
                <Stack gap="sm">
                  <Table horizontalSpacing="sm" verticalSpacing="xs" striped>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Test</Table.Th>
                        <Table.Th>Value</Table.Th>
                        <Table.Th>Reference Range</Table.Th>
                        <Table.Th>Interpretation</Table.Th>
                        <Table.Th>Category</Table.Th>
                        <Table.Th>Performer</Table.Th>
                        <Table.Th>Status</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {displayedObservations.map((obs) => {
                        const testName = formatCodeableConcept(obs.code) || 'Unknown Test';
                        const value = formatObservationValue(obs);
                        const referenceRange = obs.referenceRange?.[0] ? formatRange(obs.referenceRange[0]) : '';
                        const interpretation = formatCodeableConcept(obs.interpretation?.[0]);
                        const category = formatCodeableConcept(obs.category?.[0]);
                        const performer = obs.performer?.[0]?.display || '';
                        const status = obs.status || '';

                        return (
                          <Table.Tr key={obs.id}>
                            <Table.Td>
                              <Text size="sm" fw={500}>
                                {testName}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{value}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{referenceRange}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{interpretation}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{category}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{performer}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" tt="capitalize">
                                {status}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>

                  {observations.length > 3 && (
                    <Box>
                      <Anchor size="sm" onClick={() => setIsExpanded(!isExpanded)} component="button" type="button">
                        {isExpanded ? 'Show Less...' : 'Show More...'}
                      </Anchor>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
        </Card>
      </RestrictedContentManager>
    </HighlightableItem>
  );
}
